import { useMemo, useRef } from 'react';
import { useStore } from 'react-redux';
import useProjectReplacements from '@replacements/core/useProjectReplacements';
import applyReplacementDiff from '@replacements/utils/headlessReplacementTransform';
import { useFeatureFlags } from 'components/FeatureFlags';
import {
  buildReplacementsWithRegex,
  deduplicateByText,
} from 'components/ScriptStudio/Editor/extensions/ReplacementExtension';
import HeadlessReplacementManager from 'components/ScriptStudio/Editor/extensions/headlessReplacementManager';
import { useStudioProjectContext } from 'pages/dashboard/studio/StudioProjectProvider';
import { AppState } from 'store';
import { EnhancedPhoneticReplacement } from 'store/ReplacementsSlice';
import { setFormattedTextDrafts } from 'store/ScriptStudioBuffersSlice';
import { useAppDispatch } from 'store/hooks';
import parseApolloError from 'util/apolloError';
import { Section } from '../../../../../server/graphql/__generated.types';
import { SectionsQuery, useSectionsQuery } from '../operation.generated';
import { getSectionBaseFormattedText, SectionViewOptions } from '../selectors';
import useSectionViewOptions from './useSectionViewOptions';

interface useProjectSectionsProps {
  projectId: string;
  skip?: boolean;
  limit?: number;
  /**
   * When true, apply active project replacements to buffers for fetchMore
   * pages only. The initial page is left to library-toggle bulk update.
   */
  withReplacementMerging?: boolean;
}

export const SECTION_LIMIT = 500;

export const useEffectiveStudioLimit = (): number => {
  const { isFeatureEnabled } = useFeatureFlags();
  return isFeatureEnabled('studio-max-section-limit') ? 2500 : SECTION_LIMIT;
};

/**
 * @description merge the replacements with active replacements
 * @note this only applies for fetch more
 */
export const mapSectionsWithActiveReplacements = (
  sections: Section[],
  projectReplacements: EnhancedPhoneticReplacement[],
  draftsById: AppState['scriptStudioBuffers']['byId'] = {},
  options: SectionViewOptions = {}
): Array<{ id: string; formattedText: string }> => {
  if (sections.length === 0 || projectReplacements.length === 0) {
    return [];
  }

  const addedByText = deduplicateByText(projectReplacements);
  const addedWithRegex = buildReplacementsWithRegex(addedByText);
  const instance = new HeadlessReplacementManager();
  const updates: Array<{ id: string; formattedText: string }> = [];

  try {
    for (const section of sections) {
      const source =
        draftsById[section.id]?.formattedTextDraft ??
        getSectionBaseFormattedText(section, options);
      if (source) {
        const formattedText = applyReplacementDiff(
          source,
          new Set(),
          addedByText,
          instance,
          addedWithRegex
        );
        if (formattedText !== source) {
          updates.push({ id: section.id, formattedText });
        }
      }
    }
  } finally {
    instance.manager.destroy();
  }

  return updates;
};

/**
 * @todo: remove withReplacementMerging once we query all sections
 * note: merge utility is only additive not subtractive and is inherently
 * not stable due to this fact.
 */
const useProjectSections = ({
  projectId,
  skip = false,
  limit,
  withReplacementMerging = false,
}: useProjectSectionsProps) => {
  const dispatch = useAppDispatch();
  const store = useStore<AppState>();
  const { studioVersion } = useStudioProjectContext();
  const studioLimit = useEffectiveStudioLimit();
  const effectiveLimit = limit ?? studioLimit;
  const sectionViewOptions = useSectionViewOptions();
  const { projectReplacements } = useProjectReplacements({
    projectId: withReplacementMerging ? projectId : undefined,
    projectStudioVersion: studioVersion,
  });

  const projectReplacementsRef = useRef(projectReplacements);
  projectReplacementsRef.current = projectReplacements;

  const { data, refetch, fetchMore, loading, error } = useSectionsQuery({
    variables: { inputs: { projectId, limit: effectiveLimit } },
    skip,
  });

  const sections = useMemo(
    () => data?.sections.edges.map(item => item.node) ?? [],
    [data?.sections.edges]
  );

  return {
    sections,
    pageInfo: data?.sections.pageInfo,
    refetch,
    fetchMore: async (...args: Parameters<typeof fetchMore>) => {
      const result = await fetchMore(...args);
      if (!withReplacementMerging) return result;

      const incoming =
        (result.data as SectionsQuery | undefined)?.sections.edges.map(
          edge => edge.node
        ) ?? [];
      const updates = mapSectionsWithActiveReplacements(
        incoming,
        projectReplacementsRef.current,
        store.getState().scriptStudioBuffers.byId,
        sectionViewOptions
      );
      if (updates.length > 0) {
        dispatch(setFormattedTextDrafts({ projectId, sections: updates }));
      }
      return result;
    },
    loading,
    error: error ? parseApolloError(error) : undefined,
  };
};

export default useProjectSections;
